import { zodResolver } from '@hookform/resolvers/zod'
import { MuiTelInputInfo } from 'mui-tel-input'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import OnboardTitleTypography from './OnboardTitleTypography'
import {
  OnboardRequestContainer,
  OnboardRequestGridContainer,
  OnboardRequestMuiTelInput,
} from './styled/OnboardRequest.styled'
import {
  OnboardRequestMethods,
  OnboardRequestPayloadType,
} from '../../../types'
import { useAppSelector } from '../../../store'
import { selectLocateRestaurant } from '../../../store/onboardSlice'
import { selectUserInfo } from '../../../store/authSlice'
import { Box, Grid } from '@mui/material'
import { IconInfoSquareRounded } from '@tabler/icons-react'
import InputComponent, {
  ResactlyInputLabel,
} from '../../../components/ui/InputComponent'

const formSchema = z.object({
  restaurantName: z.string().min(1, { message: 'Restaurant name is required' }),
  contactName: z.string().min(1, { message: 'Contact name is required' }),
  contactEmail: z.string().email({ message: 'Invalid email address' }),
  phoneNumber: z.string().min(1, { message: 'Phone number is required' }),
  websiteLink: z.string().url({ message: 'Invalid URL' }),
})

interface OnboardRequestProps {
  key?: number
  title?: string
  subTitle?: string
  setNextButtonDisable: (disable: boolean) => void
}

const OnboardRequest = React.forwardRef<
  OnboardRequestMethods,
  OnboardRequestProps
>(function OnboardRequest(
  { title, subTitle, setNextButtonDisable },
  ref,
): JSX.Element {
  const locateRestaurant = useAppSelector(selectLocateRestaurant)
  const loggedUser = useAppSelector(selectUserInfo)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      phoneNumber: '',
      contactEmail: loggedUser?.email,
      websiteLink: locateRestaurant?.website,
      contactName: '',
      restaurantName: locateRestaurant?.name,
    },
  })

  useEffect(() => {
    setNextButtonDisable(!isValid)
  }, [isValid, setNextButtonDisable])

  const getPayload = async (): Promise<OnboardRequestPayloadType | {}> => {
    let payloadData: OnboardRequestPayloadType | {} = {}
    await handleSubmit((data: z.infer<typeof formSchema>): void => {
      payloadData = data
    })()
    return payloadData
  }

  React.useImperativeHandle(ref, () => ({ getPayload }))

  return (
    <>
      <OnboardTitleTypography title={title ?? ''} subTitle={subTitle ?? ''} />
      <OnboardRequestContainer>
        <OnboardRequestGridContainer>
          <Grid item xs={12} md={6}>
            <div>
              <InputComponent
                id="name"
                label="Restaurant Name"
                isFieldRequired
                labelFontSize="14px"
                labelWeight={500}
                placeholder="Enter details"
                {...register('restaurantName')}
                error={!!errors.restaurantName}
                helperText={
                  errors.restaurantName ? errors.restaurantName.message : ' '
                }
                sx={{
                  mt: '4px',
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              <InputComponent
                id="contactName"
                label="Contact Name"
                isFieldRequired
                labelFontSize="14px"
                labelWeight={500}
                placeholder="Enter contact name"
                {...register('contactName')}
                error={!!errors.contactName}
                helperText={
                  errors.contactName ? errors.contactName.message : ' '
                }
                sx={{
                  mt: '4px',
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              <InputComponent
                id="email"
                label="Contact Email"
                isFieldRequired
                labelFontSize="14px"
                labelWeight={500}
                placeholder="Enter contact email"
                {...register('contactEmail')}
                error={!!errors.contactEmail}
                helperText={
                  errors.contactEmail ? (
                    errors.contactEmail.message
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconInfoSquareRounded
                        stroke={2}
                        size={14}
                        style={{
                          marginRight: '5px',
                        }}
                      />
                      This email is used to log in to Resactly
                    </Box>
                  )
                }
                sx={{
                  mt: '4px',
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              {/* <OnboardRequestInputLabel htmlFor="phoneNumber">
                Phone Number
              </OnboardRequestInputLabel> */}
              <ResactlyInputLabel
                label="Phone Number"
                htmlFor="phoneNumber"
                isFieldRequired
                labelFontSize="14px"
                labelWeight={500}
                isValid={!!errors.phoneNumber}
              />
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <OnboardRequestMuiTelInput
                    {...field}
                    id="phoneNumber"
                    placeholder="Enter phone number"
                    {...register('phoneNumber')}
                    error={!!errors.phoneNumber}
                    helperText={
                      errors.phoneNumber ? errors.phoneNumber.message : ' '
                    }
                    onChange={(value, info: MuiTelInputInfo) =>
                      field.onChange(value)
                    }
                    sx={{
                      mt:'4px'
                    }}
                  />
                )}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              <InputComponent
                id="website"
                label="Website Link"
                isFieldRequired
                labelFontSize="14px"
                labelWeight={500}
                placeholder="Enter website link"
                {...register('websiteLink')}
                error={!!errors.websiteLink}
                helperText={
                  errors.websiteLink ? errors.websiteLink.message : ' '
                }
                sx={{
                  mt: '4px',
                }}
              />
            </div>
          </Grid>
        </OnboardRequestGridContainer>
      </OnboardRequestContainer>
    </>
  )
})

export default OnboardRequest
